import React, { useState, useEffect } from 'react';
import { toastr } from 'react-redux-toastr';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import NumberFormat from 'react-number-format';

import LoadingScreen from '../../../collums-components/components/common/loadingScreen';
import { getCurrencySymbol } from '../../../collums-components/helpers';

import {
    AppBar,
    Card,
    Button,
    Menu,
    MenuItem,
    Fade,
    Box,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TableSortLabel,
    TablePagination,
    Dialog,
    IconButton,
    Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { withStyles } from '@material-ui/core/styles';

import StyledTableRow from '../../common/StyledTableRow';
import NotFound from '../../common/NotFound';

import {
    getCustomerMembershipsForRedemption,
    redeemMembershipService,
    unredeemMembershipService,
    pauseMembership,
    restartMembership,
    cancelMembership
} from '../../../api/membershipApi';

import CancelContinueModal from '../../../collums-components/components/common/CancelContinueModal';

import styles from '../styles';
import tableColumns from './tableColumns';

const MEMBERSHIP_GROUPS = {
    active: 'Active',
    completed: 'Completed',
    expired_voided: 'Expired / Voided',
    all: 'All'
};

function CustomerMemberships({ customer, classes }) {
    const history = useHistory();
    const [group, setGroup] = useState(MEMBERSHIP_GROUPS.active);
    const [memberships, setMemberships] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [order, setOrder] = useState({ sortDirection: 'desc', orderBy: 'Start', id: 'startDate' });
    const [isLoading, setIsLoading] = useState(false);
    const [counts, setCounts] = useState({ active: 0, completed: 0, expired_voided: 0, all: 0 });

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [detailRow, setDetailRow] = useState(null);
    const [showPauseModal, setShowPauseModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showRedeemModal, setShowRedeemModal] = useState(false);
    const [showUnredeemModal, setShowUnredeemModal] = useState(false);

    const formatDate = dateStr => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = String(d.getFullYear()).slice(-2);
        return `${day}/${month}/${year}`;
    };

    async function fetchMemberships() {
        if (!customer) return;
        try {
            setIsLoading(true);
            const data = await getCustomerMembershipsForRedemption(customer.id, '', false);
            setMemberships(data || []);
            setIsLoading(false);
        } catch (e) {
            console.error(e);
            setIsLoading(false);
            toastr.error('Something went wrong loading memberships');
        }
    }

    const getExpiryDate = membership => {
        const expiryMonths = membership.membershipSnapshot?.expiryAfterMonths;
        if (!expiryMonths || !membership.startDate) return null;
        const start = new Date(membership.startDate);
        const expiry = new Date(start);
        expiry.setMonth(expiry.getMonth() + expiryMonths);
        return expiry;
    };

    const getPaymentDayOrdinal = membership => {
        if (!membership.firstPayment) return '';
        const day = new Date(membership.firstPayment).getDate();
        const suffixes = ['th', 'st', 'nd', 'rd'];
        const v = day % 100;
        const suffix = suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];
        return `${day}${suffix}`;
    };

    const getMembershipStatus = (membership, svc) => {
        const isVoided = !!svc.voidedAt;
        const isCompleted = svc.serviceType === 'one-off' && svc.isCompleted && !isVoided;
        const expiryDate = getExpiryDate(membership);
        const isExpired = expiryDate && new Date() > expiryDate && !isVoided;
        if (isVoided || isExpired) return 'expired_voided';
        if (isCompleted) return 'completed';
        return 'active';
    };

    const getDisplayStatus = (membership, svc) => {
        const isVoided = !!svc.voidedAt;
        const expiryDate = getExpiryDate(membership);
        const isExpired = expiryDate && new Date() > expiryDate && !isVoided;
        const isPaused = membership.status === 'paused';

        if (isVoided) return { label: 'Voided', color: '#bf2000' };
        if (isExpired) return { label: 'Expired', color: '#bf2000' };
        if (isPaused) return { label: 'Paused', color: '#e65100' };
        if (membership.isOverdue) return { label: 'Overdue', color: '#bf2000' };
        return { label: 'Paid', color: '#2e7d32' };
    };

    const getBookingWindow = svc => {
        if (!svc.currentPeriod) return '';
        if (svc.bookNextApptFrom) {
            const nextStart = new Date(svc.bookNextApptFrom);
            const repeatEvery = svc.repeatEvery || 1;
            const nextEnd = new Date(nextStart);
            nextEnd.setMonth(nextEnd.getMonth() + repeatEvery);
            const nextEndDisplay = new Date(nextEnd);
            nextEndDisplay.setDate(nextEndDisplay.getDate() - 1);
            return `${formatDate(nextStart)} - ${formatDate(nextEndDisplay)}`;
        }
        const periodEndDisplay = new Date(svc.currentPeriod.periodEnd);
        periodEndDisplay.setDate(periodEndDisplay.getDate() - 1);
        return `${formatDate(svc.currentPeriod.periodStart)} - ${formatDate(periodEndDisplay)}`;
    };

    const flattenRows = membershipsData => {
        const rows = [];
        for (const membership of membershipsData) {
            const expiryDate = getExpiryDate(membership);

            for (const svc of membership.services || []) {
                const groupStatus = getMembershipStatus(membership, svc);
                const displayStatus = getDisplayStatus(membership, svc);

                // Calculate total services across all periods
                let totalServices = svc.total || 0;
                const expiryMonths = membership.membershipSnapshot?.expiryAfterMonths;
                if (svc.serviceType === 'repeating') {
                    if (expiryMonths && svc.repeatEvery) {
                        const numberOfPeriods = Math.floor(expiryMonths / svc.repeatEvery);
                        totalServices = (svc.quantity || 1) * numberOfPeriods;
                    } else {
                        totalServices = 'N/a';
                    }
                }

                rows.push({
                    membershipId: membership.id,
                    membershipName: membership.membershipSnapshot?.name || membership.name,
                    serviceName: svc.service?.name || 'Unknown',
                    serviceType: svc.serviceType || 'one-off',
                    repeatEvery: svc.repeatEvery,
                    rollover: svc.rollover,
                    availableFrom: svc.availableFrom,
                    quantity: svc.quantity,
                    groupStatus,
                    displayStatus,
                    coherentStatus: membership.status,
                    total: totalServices,
                    redeemed: svc.totalRedeemed != null ? svc.totalRedeemed : svc.redeemed || 0,
                    available: svc.available || 0,
                    reserved: svc.reserved || 0,
                    lostSlots: svc.lostSlots || 0,
                    serviceIndex: svc.serviceIndex,
                    startDate: membership.startDate,
                    expiryDate,
                    currentPeriod: svc.currentPeriod,
                    periodIndex: svc.periodIndex,
                    valueEach: svc.valueEach,
                    redeemedServices:
                        (svc.enrichedRedeemed?.length ? svc.enrichedRedeemed : svc.redeemedServices) || [],
                    scheduledAppointments: svc.scheduledAppointments || [],
                    bookNextApptFrom: svc.bookNextApptFrom,
                    pricePerMonth: membership.pricePerMonth,
                    nextPaymentDue: membership.nextPaymentDue,
                    isOverdue: membership.isOverdue,
                    paymentOrdinal: getPaymentDayOrdinal(membership),
                    bookingWindow: getBookingWindow(svc)
                });
            }
        }
        return rows;
    };

    useEffect(() => {
        fetchMemberships();
        /* eslint-disable-next-line */
    }, [customer]);

    useEffect(() => {
        const rows = flattenRows(memberships);
        const active = rows.filter(r => r.groupStatus === 'active').length;
        const completed = rows.filter(r => r.groupStatus === 'completed').length;
        const expired_voided = rows.filter(r => r.groupStatus === 'expired_voided').length;
        setCounts({ active, completed, expired_voided, all: rows.length });

        let filtered = rows;
        if (group === MEMBERSHIP_GROUPS.active) filtered = rows.filter(r => r.groupStatus === 'active');
        else if (group === MEMBERSHIP_GROUPS.completed) filtered = rows.filter(r => r.groupStatus === 'completed');
        else if (group === MEMBERSHIP_GROUPS.expired_voided)
            filtered = rows.filter(r => r.groupStatus === 'expired_voided');

        setFilteredRows(filtered);
        setPage(0);
        /* eslint-disable-next-line */
    }, [memberships, group]);

    const handleSort = column => {
        if (!column.reordable) return;
        if (column.name === order.orderBy) {
            setOrder({ ...order, sortDirection: order.sortDirection === 'asc' ? 'desc' : 'asc' });
        } else {
            setOrder({ orderBy: column.name, sortDirection: 'asc', id: column.id });
        }
    };

    const sortedRows = [...filteredRows].sort((a, b) => {
        const dir = order.sortDirection === 'asc' ? 1 : -1;
        if (order.id === 'membershipSnapshot.name') {
            return dir * (a.membershipName || '').localeCompare(b.membershipName || '');
        }
        if (order.id === 'status') {
            return dir * (a.displayStatus.label || '').localeCompare(b.displayStatus.label || '');
        }
        if (order.id === 'startDate') {
            return dir * (new Date(a.startDate) - new Date(b.startDate));
        }
        return 0;
    });

    const paginatedRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const handleOpenMenu = (event, row) => {
        event.stopPropagation();
        event.preventDefault();
        setSelectedItem(row);
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const onHistory = () => {
        handleCloseMenu();
        if (!selectedItem) return;
        setDetailRow(selectedItem);
    };

    const onRedeem = () => {
        handleCloseMenu();
        if (!selectedItem) return;
        setShowRedeemModal(true);
    };

    const onRedeemConfirm = async () => {
        setShowRedeemModal(false);
        try {
            await redeemMembershipService(selectedItem.membershipId, selectedItem.serviceIndex);
            toastr.success('Service redeemed');
            await fetchMemberships();
        } catch (e) {
            console.error(e);
            toastr.error(e?.response?.data?.message || 'Failed to redeem service');
        }
    };

    const onUnredeem = () => {
        handleCloseMenu();
        if (!selectedItem) return;
        setShowUnredeemModal(true);
    };

    const onUnredeemConfirm = async () => {
        setShowUnredeemModal(false);
        try {
            await unredeemMembershipService(selectedItem.membershipId, selectedItem.serviceIndex);
            toastr.success('Last redemption undone');
            await fetchMemberships();
        } catch (e) {
            console.error(e);
            toastr.error('Failed to unredeem service');
        }
    };

    const onPauseClick = () => {
        handleCloseMenu();
        if (!selectedItem) return;
        setShowPauseModal(true);
    };

    const onPauseConfirm = async () => {
        setShowPauseModal(false);
        if (!selectedItem) return;
        try {
            await pauseMembership(selectedItem.membershipId);
            toastr.success('Membership paused');
            await fetchMemberships();
        } catch (e) {
            console.error(e);
            toastr.error('Failed to pause membership');
        }
    };

    const onRestart = async () => {
        handleCloseMenu();
        if (!selectedItem) return;
        try {
            await restartMembership(selectedItem.membershipId);
            toastr.success('Membership restarted');
            await fetchMemberships();
        } catch (e) {
            console.error(e);
            toastr.error('Failed to restart membership');
        }
    };

    const onCancelClick = () => {
        handleCloseMenu();
        if (!selectedItem) return;
        setShowCancelModal(true);
    };

    const onCancelConfirm = async () => {
        setShowCancelModal(false);
        if (!selectedItem) return;
        try {
            await cancelMembership(selectedItem.membershipId);
            toastr.success('Membership cancelled');
            await fetchMemberships();
        } catch (e) {
            console.error(e);
            toastr.error('Failed to cancel membership');
        }
    };

    const onBookAppt = () => {
        handleCloseMenu();
        if (!selectedItem || !customer) return;
        const bookingDate = selectedItem.bookNextApptFrom || selectedItem.currentPeriod?.periodStart;
        const day = bookingDate ? moment(bookingDate).format('DD-MM-YYYY') : moment().format('DD-MM-YYYY');
        history.push(`/?day=${day}&customer=${customer.id}`);
    };

    const buildDetailRows = () => {
        if (!detailRow) return [];

        const formatDateTime = dateStr => {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            return (
                d.toLocaleDateString('en-GB') +
                ' ' +
                d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
            );
        };

        const redeemedServices = detailRow.redeemedServices || [];
        const scheduledAppointments = detailRow.scheduledAppointments || [];
        const isRepeating = detailRow.serviceType === 'repeating';
        const startDate = detailRow.startDate ? new Date(detailRow.startDate) : null;

        // For non-repeating or no start date, use simple logic
        if (!isRepeating || !startDate) {
            const total =
                redeemedServices.length +
                scheduledAppointments.length +
                Math.max(
                    0,
                    (typeof detailRow.total === 'number' ? detailRow.total : 0) -
                        redeemedServices.length -
                        scheduledAppointments.length
                );
            const rows = [];
            for (let i = 0; i < total; i++) {
                let status = 'Available';
                let dateTime = '';
                let practitionerName = '';
                let clinicName = '';

                if (i < redeemedServices.length) {
                    const rs = redeemedServices[i];
                    status = rs?.appointment ? 'Completed' : 'Manually Redeemed';
                    dateTime = formatDateTime(rs?.redeemedAt);
                    practitionerName = rs?.practitionerName || '';
                    clinicName = rs?.clinicName || '';
                } else if (i < redeemedServices.length + scheduledAppointments.length) {
                    status = 'Scheduled';
                    const appt = scheduledAppointments[i - redeemedServices.length];
                    dateTime = formatDateTime(appt?.start);
                    practitionerName = appt?.practitionerName || '';
                    clinicName = appt?.clinicName || '';
                }

                rows.push({
                    index: i + 1,
                    dateTime,
                    status,
                    practitionerName,
                    bookingWindow: detailRow.bookingWindow,
                    isRollover: false,
                    clinicName
                });
            }
            return rows;
        }

        // Repeating services — build periods with rollover/missed logic
        const quantity = detailRow.quantity || 1;
        const repeatEvery = detailRow.repeatEvery || 1;
        const hasRollover = detailRow.rollover;
        const expiryDate = detailRow.expiryDate;

        // Determine number of periods
        let numberOfPeriods;
        if (expiryDate) {
            const expiryMonths =
                (new Date(expiryDate).getFullYear() - startDate.getFullYear()) * 12 +
                new Date(expiryDate).getMonth() -
                startDate.getMonth();
            numberOfPeriods = Math.floor(expiryMonths / repeatEvery);
        } else {
            // No expiry: show up to current period
            const now = new Date();
            const monthsSinceStart =
                (now.getFullYear() - startDate.getFullYear()) * 12 + now.getMonth() - startDate.getMonth();
            numberOfPeriods = Math.max(1, Math.floor(monthsSinceStart / repeatEvery) + 1);
        }

        // All appointments sorted by date
        const allAppointments = [
            ...redeemedServices.map(rs => ({
                type: rs.appointment ? 'Completed' : 'Manually Redeemed',
                date: rs.redeemedAt,
                practitionerName: rs.practitionerName || '',
                clinicName: rs.clinicName || ''
            })),
            ...scheduledAppointments.map(sa => ({
                type: 'Scheduled',
                date: sa.start,
                practitionerName: sa.practitionerName || '',
                clinicName: sa.clinicName || ''
            }))
        ].sort((a, b) => new Date(a.date) - new Date(b.date));

        const now = new Date();
        const rows = [];
        const apptQueue = [...allAppointments];
        let prevUnusedFromOwnQty = 0;

        for (let p = 0; p < numberOfPeriods; p++) {
            const periodStart = new Date(startDate);
            periodStart.setMonth(periodStart.getMonth() + p * repeatEvery);
            const periodEnd = new Date(periodStart);
            periodEnd.setMonth(periodEnd.getMonth() + repeatEvery);
            const windowPassed = now > periodEnd;
            const periodEndDisplay = new Date(periodEnd);
            periodEndDisplay.setDate(periodEndDisplay.getDate() - 1);
            const bookingWindow = `${formatDate(periodStart)} - ${formatDate(periodEndDisplay)}`;

            const prevPeriodPassed =
                p > 0 &&
                (() => {
                    const prevEnd = new Date(startDate);
                    prevEnd.setMonth(prevEnd.getMonth() + p * repeatEvery);
                    return now > prevEnd;
                })();
            const carriedOver = hasRollover && prevPeriodPassed ? prevUnusedFromOwnQty : 0;
            const totalSlots = quantity + carriedOver;

            let consumedInPeriod = 0;

            for (let s = 0; s < totalSlots; s++) {
                const isCarriedOverSlot = s >= quantity;
                let status = 'Available';
                let dateTime = '';
                let practitionerName = '';
                let clinicName = '';
                let isRollover = isCarriedOverSlot;

                if (apptQueue.length > 0) {
                    const appt = apptQueue.shift();
                    status = appt.type;
                    dateTime = formatDateTime(appt.date);
                    practitionerName = appt.practitionerName;
                    clinicName = appt.clinicName;
                    consumedInPeriod++;
                } else if (windowPassed) {
                    status = 'Missed';
                }

                rows.push({
                    index: rows.length + 1,
                    dateTime,
                    status,
                    practitionerName,
                    bookingWindow,
                    isRollover,
                    clinicName
                });
            }

            // Only own quantity (not carried over) can roll forward — only from passed periods
            if (windowPassed) {
                const consumedFromOwn = Math.min(quantity, consumedInPeriod);
                prevUnusedFromOwnQty = Math.max(0, quantity - consumedFromOwn);
            } else {
                prevUnusedFromOwnQty = 0;
            }
        }

        return rows;
    };

    const historyHeaderStyle = {
        fontSize: 14,
        fontWeight: 500,
        borderColor: 'black',
        borderWidth: 2
    };

    const renderDetailModal = () => {
        if (!detailRow) return null;
        const period = detailRow.currentPeriod;
        const detailRows = buildDetailRows();

        return (
            <Dialog
                open={!!detailRow}
                maxWidth={false}
                onBackdropClick={() => setDetailRow(null)}
                onEscapeKeyDown={() => setDetailRow(null)}
                PaperProps={{ style: { maxWidth: 1100, width: '95%' } }}
            >
                <div
                    style={{
                        width: '100%',
                        backgroundColor: 'white',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '0 16px',
                        height: 'min-content'
                    }}
                >
                    <IconButton
                        style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}
                        onClick={() => setDetailRow(null)}
                    >
                        <CloseIcon />
                    </IconButton>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'column',
                            marginBottom: 24
                        }}
                    >
                        <p
                            style={{
                                fontFamily: 'Roboto, Arial, Sans-serif',
                                fontWeight: 500,
                                fontSize: 18,
                                margin: 0
                            }}
                        >
                            Membership History
                        </p>
                        {detailRow.isOverdue && (
                            <p style={{ color: '#bf2000', fontWeight: 500, fontSize: 18, margin: '0 0 14px 0' }}>
                                OVERDUE
                            </p>
                        )}
                        <Typography style={{ fontSize: 14, marginTop: 8 }}>
                            {detailRow.membershipName} - {detailRow.serviceName}
                        </Typography>
                        <Typography style={{ fontSize: 14, marginTop: 4 }}>
                            {detailRow.serviceType === 'repeating'
                                ? `${detailRow.quantity || 1} every ${detailRow.repeatEvery || 1} month(s)`
                                : `One-off x${detailRow.quantity || 1}`}
                            {detailRow.rollover ? ' (rollover)' : ''}
                        </Typography>
                        {period && (
                            <Typography style={{ fontSize: 14, marginTop: 4 }}>
                                Current Period: {formatDate(period.periodStart)} -{' '}
                                {(() => {
                                    const endDisplay = new Date(period.periodEnd);
                                    endDisplay.setDate(endDisplay.getDate() - 1);
                                    return formatDate(endDisplay);
                                })()}
                                {period.carriedOver > 0 && ` (+${period.carriedOver} carried over)`}
                            </Typography>
                        )}
                    </div>
                    <div style={{ width: '95%', display: 'flex', justifyContent: 'space-around', marginBottom: 50 }}>
                        {detailRows.length > 0 ? (
                            <Table style={{ width: '100%' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={historyHeaderStyle}>Date/Time</TableCell>
                                        <TableCell style={historyHeaderStyle}>#</TableCell>
                                        <TableCell style={historyHeaderStyle}>Service</TableCell>
                                        <TableCell style={historyHeaderStyle}>Status</TableCell>
                                        <TableCell style={historyHeaderStyle}>Redemption value</TableCell>
                                        <TableCell style={historyHeaderStyle}>Practitioner</TableCell>
                                        <TableCell style={historyHeaderStyle}>Booking window</TableCell>
                                        <TableCell style={historyHeaderStyle}>Roll over</TableCell>
                                        <TableCell style={historyHeaderStyle}>Location</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {detailRows.map(row => (
                                        <StyledTableRow key={row.index}>
                                            <TableCell>{row.dateTime}</TableCell>
                                            <TableCell>{row.index}</TableCell>
                                            <TableCell>{detailRow.serviceName}</TableCell>
                                            <TableCell>{row.status}</TableCell>
                                            <TableCell>
                                                <NumberFormat
                                                    value={(detailRow.valueEach || 0).toFixed(2)}
                                                    fixedDecimalScale
                                                    decimalScale={2}
                                                    displayType={'text'}
                                                    thousandSeparator={true}
                                                    prefix={getCurrencySymbol()}
                                                />
                                            </TableCell>
                                            <TableCell>{row.practitionerName}</TableCell>
                                            <TableCell>{row.bookingWindow}</TableCell>
                                            <TableCell>{row.isRollover ? 'Roll over' : ''}</TableCell>
                                            <TableCell>{row.clinicName}</TableCell>
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <Typography variant="h5">No redemption data for this service.</Typography>
                        )}
                    </div>
                </div>
            </Dialog>
        );
    };

    const getFreqLabel = row => {
        if (row.serviceType === 'repeating') {
            return `${row.quantity || 1} every ${row.repeatEvery || 1} month${(row.repeatEvery || 1) > 1 ? 's' : ''}`;
        }
        return `One-off x${row.quantity || 1}`;
    };

    return (
        <div>
            {isLoading && <LoadingScreen />}
            <AppBar className={classes.whiteBar} classes={{ root: classes.noShadow }} position="static">
                <Tabs
                    classes={{ indicator: classes.tabIndicator }}
                    centered
                    variant="scrollable"
                    value={group}
                    onChange={(e, value) => setGroup(value)}
                >
                    <Tab
                        className={classes.tab}
                        value={MEMBERSHIP_GROUPS.active}
                        disabled={!counts.active}
                        label={`${MEMBERSHIP_GROUPS.active} (${counts.active})`}
                    />
                    <Tab
                        className={classes.tab}
                        value={MEMBERSHIP_GROUPS.completed}
                        disabled={!counts.completed}
                        label={`${MEMBERSHIP_GROUPS.completed} (${counts.completed})`}
                    />
                    <Tab
                        className={classes.tab}
                        value={MEMBERSHIP_GROUPS.expired_voided}
                        disabled={!counts.expired_voided}
                        label={`${MEMBERSHIP_GROUPS.expired_voided} (${counts.expired_voided})`}
                    />
                    <Tab
                        className={classes.tab}
                        value={MEMBERSHIP_GROUPS.all}
                        label={`${MEMBERSHIP_GROUPS.all} (${counts.all})`}
                    />
                </Tabs>
            </AppBar>
            <Card style={{ background: '#f7f7f7' }}>
                {paginatedRows.length > 0 ? (
                    <>
                        <Box className={classes.electronicNotesTableGrid}>
                            <Table size="medium" stickyHeader style={{ padding: 20 }}>
                                <TableHead>
                                    <TableRow>
                                        {tableColumns.map(column => (
                                            <TableCell
                                                sortDirection={
                                                    column.name === order.orderBy ? order.sortDirection : false
                                                }
                                                align={column.numeric ? 'right' : 'left'}
                                                key={column.name}
                                                className={classes.tableHeader}
                                            >
                                                {column.reordable ? (
                                                    <TableSortLabel
                                                        active={column.name === order.orderBy}
                                                        direction={order.sortDirection}
                                                        onClick={() => handleSort(column)}
                                                    >
                                                        {column.name}
                                                    </TableSortLabel>
                                                ) : (
                                                    column.name
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedRows.map((row, index) => (
                                        <StyledTableRow
                                            className={classes.tableRow}
                                            key={`${row.membershipId}-${row.serviceIndex}-${index}`}
                                            hover
                                            onClick={() => setDetailRow(row)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <TableCell className={classes.tableBodyCellLarge}>
                                                <b>{row.membershipName}</b>
                                            </TableCell>
                                            <TableCell className={classes.tableBodyCellLarge}>
                                                {row.serviceName}
                                            </TableCell>
                                            <TableCell className={classes.tableBodyCellLarge}>
                                                {formatDate(row.startDate)}
                                            </TableCell>
                                            <TableCell className={classes.tableBodyCellLarge}>
                                                {row.expiryDate ? formatDate(row.expiryDate) : 'N/a'}
                                            </TableCell>
                                            <TableCell className={classes.tableBodyCellLarge}>
                                                {getFreqLabel(row)}
                                            </TableCell>
                                            <TableCell className={classes.tableBodyCellLarge}>
                                                <NumberFormat
                                                    value={row.pricePerMonth || 0}
                                                    fixedDecimalScale
                                                    decimalScale={2}
                                                    displayType={'text'}
                                                    thousandSeparator={true}
                                                    prefix={getCurrencySymbol()}
                                                />
                                            </TableCell>
                                            <TableCell className={classes.tableBodyCellLarge}>
                                                {row.paymentOrdinal}
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableBodyCellLarge}
                                                style={{ color: row.displayStatus.color, fontWeight: 500 }}
                                            >
                                                {row.displayStatus.label}
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableBodyCellLarge}
                                                style={{ textAlign: 'end' }}
                                            >
                                                {row.total}
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableBodyCellLarge}
                                                style={{ textAlign: 'end' }}
                                            >
                                                {row.redeemed}
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableBodyCellLarge}
                                                style={{ textAlign: 'end' }}
                                            >
                                                {row.total === 'N/a'
                                                    ? 'N/a'
                                                    : Math.max(0, row.total - row.redeemed - row.lostSlots)}
                                            </TableCell>
                                            <TableCell className={classes.tableBodyCellLarge}>
                                                {row.bookingWindow}
                                            </TableCell>
                                            <TableCell className={classes.tableBodyCellLarge}>
                                                <Button
                                                    className={classes.optionsButton}
                                                    variant="outlined"
                                                    onClick={e => handleOpenMenu(e, row)}
                                                >
                                                    Options
                                                </Button>
                                                <Menu
                                                    anchorEl={anchorEl}
                                                    open={
                                                        !!anchorEl &&
                                                        selectedItem?.membershipId === row.membershipId &&
                                                        selectedItem?.serviceIndex === row.serviceIndex
                                                    }
                                                    TransitionComponent={Fade}
                                                    className={classes.optionsMenu}
                                                    onClose={handleCloseMenu}
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                    }}
                                                >
                                                    <MenuItem onClick={onHistory}>History</MenuItem>
                                                    <MenuItem onClick={onBookAppt}>Book appt</MenuItem>
                                                    <MenuItem onClick={onRedeem}>Redeem service</MenuItem>
                                                    <MenuItem onClick={onUnredeem}>Unredeem last service</MenuItem>
                                                    {row.coherentStatus === 'paused' ? (
                                                        <MenuItem onClick={onRestart}>Restart</MenuItem>
                                                    ) : (
                                                        <MenuItem onClick={onPauseClick}>Pause</MenuItem>
                                                    )}
                                                    <MenuItem onClick={onCancelClick} style={{ color: '#d32f2f' }}>
                                                        Cancel Membership
                                                    </MenuItem>
                                                </Menu>
                                            </TableCell>
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50]}
                            component="div"
                            count={sortedRows.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onChangePage={(e, newPage) => setPage(newPage)}
                            onChangeRowsPerPage={e => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            classes={{ root: classes.tablePagination }}
                        />
                    </>
                ) : (
                    <NotFound />
                )}
            </Card>
            {renderDetailModal()}

            {showPauseModal && (
                <CancelContinueModal
                    title={'Pause Membership'}
                    contentHtml={
                        '<p>Pausing this membership will pause the direct debit and no further payments will be taken until the membership is restarted.</p>' +
                        '<p style="margin-top:8px">No further appointments can be booked from this membership while paused.</p>' +
                        '<p style="margin-top:8px;font-weight:500">Don\'t forget to cancel appointments in diary if necessary.</p>' +
                        '<p style="margin-top:8px">Are you sure you wish to continue?</p>'
                    }
                    continueButtonText={'Pause Membership'}
                    onContinue={onPauseConfirm}
                    onCancel={() => setShowPauseModal(false)}
                />
            )}

            {showCancelModal && (
                <CancelContinueModal
                    title={'Cancel Membership'}
                    contentHtml={
                        '<p style="font-weight:700;color:#d32f2f;margin-bottom:8px">THIS CANNOT BE UNDONE</p>' +
                        '<p>Cancelling this membership will void the direct debit and cancel all future appointments booked under this membership.</p>' +
                        '<p style="margin-top:8px">Cancellation notifications will be sent to the customer for each cancelled appointment.</p>' +
                        '<p style="margin-top:8px">Are you sure you wish to cancel this membership?</p>'
                    }
                    continueButtonText={'Cancel Membership'}
                    onContinue={onCancelConfirm}
                    onCancel={() => setShowCancelModal(false)}
                />
            )}
            {showRedeemModal && (
                <CancelContinueModal
                    title={'Redeem Last Service'}
                    contentText={
                        'This will change the status of the next available service to "unavailable" so that it cannot be booked or taken.'
                    }
                    continueButtonText={'Redeem'}
                    onContinue={onRedeemConfirm}
                    onCancel={() => setShowRedeemModal(false)}
                />
            )}
            {showUnredeemModal && (
                <CancelContinueModal
                    title={'Unredeem Last Service'}
                    contentText={
                        'This will change the status of the last redeemed service to "available" so that it can be booked and taken again.'
                    }
                    continueButtonText={'Unredeem'}
                    onContinue={onUnredeemConfirm}
                    onCancel={() => setShowUnredeemModal(false)}
                />
            )}
        </div>
    );
}

CustomerMemberships.propTypes = {
    classes: PropTypes.object.isRequired,
    customer: PropTypes.object
};

export default withStyles(styles)(CustomerMemberships);
